import { HelpTopic } from './helpContent';

export const gheHelpTopics: HelpTopic[] = [
  {
    id: 'ghe-overview',
    label: 'Overview',
    title: 'GCSREToolkit — Overview',
    intro:
      'The GCSREToolkit is a five-page workflow for building CO2/brine relative permeability and capillary pressure curves from target rock petrophysics and reservoir conditions. It is designed for CO2 geological storage (CCS) reservoir simulation setup and produces Kr/Pc tables, analogue property tables, and IX simulator tables.',
    sections: [
      {
        id: 'overview-what',
        title: 'What the toolkit produces',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Drainage and imbibition Kr curves (KrCO2 and Krw) per target rock — ready for reservoir simulator input.',
              'Capillary pressure curves (Pc, Brooks-Corey form) per target rock.',
              'Analogue sample tables with similarity metrics, rock quality indices, fluid properties, and dimensionless numbers.',
              'IX simulator tables: Enthalpy, K-values (Crookston), and CO2 Solubility — downloaded as CSV.',
            ],
          },
        ],
      },
      {
        id: 'overview-rocks',
        title: 'Target rocks — GHE1 through GHE10',
        blocks: [
          {
            type: 'paragraph',
            text: 'Up to 10 rock intervals can be analyzed in a single session. Each rock is labeled GHE1–GHE10 and requires only porosity and permeability as inputs — all Kr, Pc, and fluid parameters are derived automatically. Results are independent per rock.',
          },
          {
            type: 'note',
            title: 'One set of curves per rock',
            text: 'Each GHE rock produces its own drainage Kr, imbibition Kr, and Pc curve set. Reservoir conditions (pressure, temperature, salinity) are shared across all rocks in the session.',
          },
        ],
      },
      {
        id: 'overview-flow',
        title: 'Five-page sequential flow',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Page 1 — GHE Inputs: enter target rock petrophysics and reservoir conditions.',
              'Page 2 — ML vs Analogues: review Kr and Pc curves with ML base and analogue ensemble.',
              'Page 3 — Analogue Tables: inspect analogue sample properties and similarity scores.',
              'Page 4 — Krel/Pc Selection: select analogues per process and export to Excel.',
              'Page 5 — IX Tables: generate and download Enthalpy, K-values, or CO2 Solubility tables (independent of Pages 1–4).',
            ],
          },
          {
            type: 'note',
            title: 'Page 5 is independent',
            text: 'IX Tables (Page 5) can be used without completing any of the other pages. It does not require GHE inputs to be saved first.',
          },
        ],
      },
      {
        id: 'overview-fluid',
        title: 'Fluid system',
        blocks: [
          {
            type: 'bullets',
            items: [
              'CO2 is the non-wetting phase — it occupies larger pore throats first during drainage (CO2 injection into brine-saturated rock).',
              'Brine is the wetting phase — it re-imbibes into the pore system after CO2 injection stops.',
              'Capillary pressure (Pc) describes the CO2–brine interface pressure at a given water saturation.',
              'The viscosity ratio (μ_brine / μ_CO₂) is typically > 1, giving CO2 a favorable mobility advantage over brine.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'ghe-inputs',
    label: 'GHE Inputs',
    title: 'GHE Inputs — Page 1',
    intro:
      'Page 1 is where all target rock petrophysics and reservoir conditions are entered. Every downstream calculation — Kr curves, Pc curves, analogue tables, and fluid properties — derives from these inputs. Save Page 1 before opening any other page.',
    sections: [
      {
        id: 'inputs-rocks',
        title: 'Target Rock Inputs (GHE1–GHE10)',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Porosity (φ)',
                description: 'Fraction of rock volume available for fluids. Dimensionless, range 0–1.',
                effect: 'Controls Swi (higher φ → lower Swi), Sco2i, pe_kpa (higher φ → lower entry pressure), and krco2_swi. The single most influential input for curve shape.',
              },
              {
                name: 'Permeability (k, mD)',
                description: 'Absolute permeability of the rock. Must be ≥ 0 mD.',
                effect: 'Drives flow_factor = log10(k + 1) / 3, which controls co2_exp, lambda_bc, and pe_kpa. Higher k → higher flow_factor → CO2 flows more easily, steeper Pc curve.',
              },
              {
                name: 'Analogues Count',
                description: 'Number of analogue parameter sets to generate around the ML base estimate. Enter an integer ≥ 1, or "auto" (= 3).',
                effect: '"Auto" (default 3) is recommended for quick analysis. Higher counts produce wider uncertainty envelopes in Kr/Pc charts but increase computation time.',
              },
            ],
          },
        ],
      },
      {
        id: 'inputs-conditions',
        title: 'Reservoir Conditions',
        blocks: [
          {
            type: 'paragraph',
            text: 'Reservoir conditions are shared across all target rocks in the session. They drive the derivation of IFT, viscosity ratio, CO2 density, and brine density.',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'NaCl (ppm)',
                description: 'Sodium chloride concentration of the formation brine.',
                effect: 'Increases IFT and viscosity ratio. Higher NaCl → higher capillary entry pressure (pe_kpa). Counted at 1× weight in the salinity term.',
              },
              {
                name: 'MgCl₂ (ppm)',
                description: 'Magnesium chloride concentration of the formation brine.',
                effect: 'Counted at 2× weight versus NaCl in the salinity term — twice the ionic effect per ppm. Combined with NaCl, controls brine ionic strength.',
              },
              {
                name: 'Pressure (MPa)',
                description: 'Reservoir pressure. Must be positive (> 0 MPa).',
                effect: 'Slightly increases IFT and viscosity ratio. Used to estimate CO2 density and brine density. Higher pressure → slightly higher pe_kpa.',
              },
              {
                name: 'Temperature (°C)',
                description: 'Reservoir temperature.',
                effect: 'Decreases IFT with temperature — higher temperature → lower pe_kpa → easier CO2 pore entry. Also affects CO2 density (CO2 is less dense at higher temperature).',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'ghe-ml',
    label: 'ML vs Analogues',
    title: 'ML vs Analogues — Page 2',
    intro:
      'Page 2 displays drainage Kr, imbibition Kr, capillary pressure (Pc), and fractional flow curves for each target rock. For each chart, two series are shown: the ML base prediction and the analogue ensemble (N curves distributed around the ML base). Together they form an uncertainty envelope.',
    sections: [
      {
        id: 'ml-derived',
        title: 'Derived fluid properties',
        blocks: [
          {
            type: 'paragraph',
            text: 'Before curve generation, fluid properties are derived from the reservoir conditions entered on Page 1:',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'IFT (Interfacial Tension, mN/m)',
                description: 'IFT = 28 + 12 × salt_term + 0.35 × P − 0.08 × T, where salt_term = NaCl_ppm/100000 + 2 × MgCl2_ppm/100000.',
                effect: 'Controls pe_kpa (entry pressure). Higher IFT → tighter entry pressure → CO2 harder to inject. Clipped to [5, 80] mN/m.',
              },
              {
                name: 'Viscosity Ratio (μ_brine / μ_CO₂)',
                description: 'viscosity_ratio = 0.65 + 5 × salt_term + 0.04 × P − 0.006 × T.',
                effect: 'Drives fractional flow. Higher viscosity ratio → CO2 displaces brine more favorably. Clipped to [0.05, 20].',
              },
              {
                name: 'Brine Density (kg/m³)',
                description: 'brine_density = clip(995 + 25 × salinity_factor + 0.9 × P, 950, 1250).',
                effect: 'Used in analogue table fluid property columns.',
              },
              {
                name: 'CO2 Density (kg/m³)',
                description: 'co2_density = clip(420 + 8 × P − 0.9 × T, 80, 1200).',
                effect: 'Used in analogue table fluid property columns.',
              },
            ],
          },
        ],
      },
      {
        id: 'ml-base-params',
        title: 'Base parameters derived per rock',
        blocks: [
          {
            type: 'paragraph',
            text: 'For each target rock, a set of base Kr and Pc parameters is derived from its porosity, permeability, and the shared fluid properties. These parameters drive all curve generation:',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Swi — Irreducible Water Saturation',
                description: 'Swi = clip(0.08 + (1 − φ) × 0.25, 0.05, 0.45).',
                effect: 'Lower bound of drainage and imbibition saturation range. Higher porosity → lower Swi → more pore space available for CO2.',
              },
              {
                name: 'Sco2i — Residual CO2 Saturation',
                description: 'Sco2i = clip(0.15 + (1 − φ) × 0.2, 0.05, 0.4).',
                effect: 'Minimum CO2 saturation that cannot be displaced when brine re-imbibes. Upper limit of imbibition range = 1 − Sco2i.',
              },
              {
                name: 'flow_factor (internal)',
                description: 'flow_factor = clip(log10(k + 1) / 3, 0, 1.2).',
                effect: 'Intermediate variable derived from permeability. Drives co2_exp, lambda_bc, and krco2_swi. Higher permeability → higher flow_factor.',
              },
              {
                name: 'co2_exp — CO2 Corey Exponent',
                description: 'co2_exp = clip(1.3 + 0.8 × flow_factor + 0.1 × ift_factor, 1.1, 3.0).',
                effect: 'Controls how steeply KrCO2 drops as Sw increases. Higher co2_exp → KrCO2 drops more steeply with Sw.',
              },
              {
                name: 'water_exp — Brine Corey Exponent',
                description: 'water_exp = clip(1.6 + 0.4 × (1 − φ) + 0.2 × visco_factor, 1.2, 3.2).',
                effect: 'Controls how steeply Krw rises with Sw. Higher water_exp → Krw rises more slowly from Swi.',
              },
              {
                name: 'krco2_swi — CO2 Endpoint Kr',
                description: 'krco2_swi = clip(0.78 − 0.25 × φ + 0.1 × flow_factor, 0.15, 0.95).',
                effect: 'Maximum KrCO2 at Swi — the CO2 relative permeability when brine is at irreducible saturation.',
              },
              {
                name: 'krw_sw1 — Brine Kr at Full Saturation',
                description: 'krw_sw1 = clip(0.65 + 0.25 × φ, 0.4, 1.0).',
                effect: 'Krw at Sw = 1 (full brine saturation). Used as the brine endpoint in drainage curves.',
              },
              {
                name: 'krw_sco2i — Brine Kr at Residual CO2',
                description: 'krw_sco2i = clip(0.55 + 0.20 × φ, 0.2, 1.0).',
                effect: 'Krw at Sw = 1 − Sco2i. Used as the brine endpoint in imbibition curves.',
              },
              {
                name: 'pe_kpa — Entry Pressure (kPa)',
                description: 'pe_kpa = clip(IFT × 6.0 / φ, 10, 3000).',
                effect: 'Capillary entry pressure — the minimum CO2 pressure needed to displace brine from the largest pore throats. Higher IFT or lower φ → higher pe_kpa.',
              },
              {
                name: 'lambda_bc — Brooks-Corey Lambda',
                description: 'lambda_bc = clip(1.7 + 0.6 × flow_factor + 0.15 × visco_factor, 0.8, 4.0).',
                effect: 'Pore size distribution index controlling Pc curve steepness. Higher lambda_bc → narrower pore size distribution → steeper Pc curve.',
              },
            ],
          },
        ],
      },
      {
        id: 'ml-drainage',
        title: 'Drainage Relative Permeability',
        blocks: [
          {
            type: 'paragraph',
            text: 'Drainage describes CO2 injection into brine-saturated rock. Water saturation decreases from 1 toward Swi as CO2 displaces brine.',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Swd (normalized drainage saturation)',
                description: 'Swd = (Sw − Swi) / (1 − Swi). Ranges from 0 at Swi to 1 at Sw = 1.',
                effect: 'Used as the normalized independent variable for both KrCO2 and Krw in drainage.',
              },
              {
                name: 'KrCO2(Sw) = krco2_swi × (1 − Swd)^co2_exp',
                description: 'CO2 relative permeability as a function of water saturation.',
                effect: 'At Sw = Swi: KrCO2 = krco2_swi (maximum CO2 mobility). At Sw = 1: KrCO2 = 0 (CO2 immobile at full brine saturation).',
              },
              {
                name: 'Krw(Sw) = krw_sw1 × Swd^water_exp',
                description: 'Brine relative permeability as a function of water saturation.',
                effect: 'At Sw = Swi: Krw = 0 (brine immobile). At Sw = 1: Krw = krw_sw1 (maximum brine mobility).',
              },
            ],
          },
        ],
      },
      {
        id: 'ml-imbibition',
        title: 'Imbibition Relative Permeability',
        blocks: [
          {
            type: 'paragraph',
            text: 'Imbibition describes brine re-invasion after CO2 injection stops. Water saturation increases from Swi toward (1 − Sco2i) as brine displaces CO2.',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Swn (normalized imbibition saturation)',
                description: 'Swn = (Sw − Swi) / (1 − Swi − Sco2i). Ranges from 0 at Swi to 1 at Sw = 1 − Sco2i.',
                effect: 'Used as the normalized independent variable for both KrCO2 and Krw in imbibition.',
              },
              {
                name: 'KrCO2(Sw) = krco2_swi × (1 − Swn)^co2_exp',
                description: 'CO2 relative permeability during imbibition.',
                effect: 'At Sw = Swi: KrCO2 = krco2_swi. At Sw = 1 − Sco2i: KrCO2 = 0 (CO2 trapped at residual saturation).',
              },
              {
                name: 'Krw(Sw) = krw_sco2i × Swn^water_exp',
                description: 'Brine relative permeability during imbibition.',
                effect: 'At Sw = Swi: Krw = 0. At Sw = 1 − Sco2i: Krw = krw_sco2i (maximum brine mobility in imbibition).',
              },
            ],
          },
        ],
      },
      {
        id: 'ml-pc',
        title: 'Capillary Pressure (Brooks-Corey)',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Pc(Sw) = pe_kpa × Swd^(−1/lambda_bc)  [kPa]',
                description: 'Brooks-Corey capillary pressure as a function of water saturation.',
                effect: 'At Sw = Swi: Pc → maximum (tightest pore throats). At Sw = 1: Pc → pe_kpa (entry pressure). Higher lambda_bc → steeper curve → well-sorted pore system. Lower lambda_bc → flatter curve → heterogeneous pore sizes.',
              },
            ],
          },
        ],
      },
      {
        id: 'ml-fw',
        title: 'Fractional Flow',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Fw(Sw) = 1 / (1 + viscosity_ratio × (KrCO2 / Krw))',
                description: 'Fraction of total flow that is brine at a given water saturation.',
                effect: 'Fw = 0 at Swi (only CO2 flows). Fw = 1 at 1 − Sco2i (only brine flows). Higher viscosity ratio → Fw rises more steeply → CO2 is more mobile relative to brine.',
              },
            ],
          },
        ],
      },
      {
        id: 'ml-series',
        title: 'ML vs Analogue series',
        blocks: [
          {
            type: 'bullets',
            items: [
              'ML series (base): single curve computed from the ML-derived base parameters for the exact porosity/permeability entered on Page 1.',
              'Analogue series: N curves where each parameter is offset proportionally around the base — co2_exp ±2.5% per step, pe_kpa ±4% per step, lambda_bc ±3% per step, and others similarly.',
              'Together the analogue curves form an uncertainty envelope around the ML prediction.',
              'The best-fit analogue (used in export) is the arithmetic mean of all N analogue parameter sets.',
            ],
          },
        ],
      },
      {
        id: 'ml-pca-diagnostics',
        title: 'Unseen Samples — PCA Diagnostics',
        blocks: [
          {
            type: 'paragraph',
            text: 'When unseen inference has been run for the project, a banner on Pages 2, 3, and 4 reports “Unseen diagnostics points available: N”. The diagnostic indicates whether the unseen samples fall within the feature space covered by the reference data.',
          },
          {
            type: 'note',
            title: 'What are unseen samples?',
            text: 'Unseen samples are petrophysical data points (e.g., from well logs or plug measurements) that were not part of the ML training data. They are submitted via the unseen inference endpoint and receive ML-predicted Kr/Pc parameters along with a PCA-based coverage check.',
          },
          {
            type: 'paragraph',
            text: 'The PCA diagnostic projects both the reference dataset and the unseen samples into a 2D space using the seven input features: porosity, permeability, pressure, temperature, NaCl concentration, MgCl2 concentration, and FZI. This projection reveals whether unseen samples lie within or outside the region covered by the training data.',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'PC1 / PC2',
                description: 'Coordinates of each unseen sample in the 2D PCA space.',
                effect: 'Samples that plot far from the reference cluster (high distance_to_reference) are extrapolations — ML predictions for those samples carry higher uncertainty.',
              },
              {
                name: 'distance_to_reference',
                description: 'Euclidean distance from the unseen sample point to the centroid of the reference data in PCA space.',
                effect: 'Low distance (< 1): sample is within the reference cloud — predictions are interpolations. High distance (> 2): sample is outside training coverage — treat predictions as extrapolations and validate against analogues.',
              },
              {
                name: 'PC1 / PC2 explained variance ratio',
                description: 'Fraction of the total feature variance captured by each principal component.',
                effect: 'A high combined ratio (PC1 + PC2 > 0.7) means the 2D plot captures most of the feature space. A low ratio means relevant variation is hidden in higher dimensions.',
              },
              {
                name: 'reference_source',
                description: '"literature_database" if the literature database file is available; "session_targets" if only the current session\'s target rocks are used as the reference.',
                effect: 'A literature_database reference provides a broader, more representative coverage check. A session_targets reference only compares against the rocks entered on Page 1.',
              },
              {
                name: 'diagnostic_flags',
                description: 'Quality flags set during inference, e.g., "non_finite_pca_sanitized" or "invalid_rock_id_not_in_session".',
                effect: 'Flagged samples had numerical issues during PCA projection and were set to zero. Treat their distance_to_reference values as unreliable.',
              },
            ],
          },
          {
            type: 'note',
            title: 'Diagnostics are cached per project',
            text: 'Run unseen inference again whenever you need to refresh the diagnostic count.',
          },
        ],
      },
    ],
  },
  {
    id: 'ghe-tables',
    label: 'Analogue Tables',
    title: 'Analogue Tables — Page 3',
    intro:
      'Page 3 shows one table per target rock. Each row is one analogue sample — a rock from the database whose pore geometry is similar to the target rock. Tables are organized by process (Drainage, Imbibition, Capillary Pressure). Use this page to assess analogue quality before selecting curves on Page 4.',
    sections: [
      {
        id: 'tables-identification',
        title: 'Identification and similarity',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'sample_id',
                description: 'Unique identifier of the analogue sample in the database.',
                effect: 'Used to trace the analogue back to its source measurement.',
              },
              {
                name: 'analogous_test_for',
                description: 'The process this analogue was matched to: drainage, imbibition, or pc.',
                effect: 'An analogue can appear in multiple process sections if it is a good match for more than one process.',
              },
              {
                name: 'rock_type',
                description: 'Lithology or rock classification label of the analogue sample.',
                effect: 'Use to check whether analogues are geologically appropriate for the target reservoir.',
              },
              {
                name: 'similarity',
                description: 'Score 0–1. Computed as 1 − (4 × |Δφ| + 0.25 × |Δlog k|) between the analogue and target rock.',
                effect: 'Higher score = closer pore geometry match. Scores > 0.5 indicate useful analogues. Scores < 0.3 suggest the analogue is a poor match and should be deselected on Page 4.',
              },
            ],
          },
        ],
      },
      {
        id: 'tables-petrophys',
        title: 'Petrophysical properties',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Porosity (fraction)',
                description: 'Analogue sample porosity.',
                effect: 'Compare against target rock porosity — should be close for a high-similarity analogue.',
              },
              {
                name: 'Permeability (mD)',
                description: 'Analogue sample absolute permeability.',
                effect: 'Together with porosity, determines RQI and FZI.',
              },
              {
                name: 'RQI (μm)',
                description: 'Reservoir Quality Index = 0.0314 × √(k/φ).',
                effect: 'Single-number flow quality indicator. Higher RQI → better pore-scale flow capacity.',
              },
              {
                name: 'FZI',
                description: 'Flow Zone Indicator = RQI / PHIZ, where PHIZ = φ/(1−φ).',
                effect: 'Decouples lithology from pore geometry. Used as the primary analogue similarity axis.',
              },
              {
                name: 'R35 Winland (μm)',
                description: 'Winland pore throat radius at 35% mercury saturation.',
                effect: 'Characterizes dominant pore throat size. Larger R35 → larger throats → lower entry pressure.',
              },
              {
                name: 'R35 Pitman (μm)',
                description: 'Pitman pore throat radius — alternative estimator of dominant pore throat size.',
                effect: 'Use alongside Winland R35 to assess pore throat size consistency.',
              },
            ],
          },
        ],
      },
      {
        id: 'tables-fluid',
        title: 'Fluid properties',
        blocks: [
          {
            type: 'paragraph',
            text: 'All fluid properties are computed from the reservoir conditions entered on Page 1 (pressure, temperature, salinity). They are the same for all analogues in a session.',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'CO2 Viscosity (cP)',
                description: 'CO2 viscosity under reservoir P-T conditions.',
                effect: 'Lower CO2 viscosity → more favorable mobility ratio → CO2 flows more easily.',
              },
              {
                name: 'CO2 Density (kg/m³)',
                description: 'CO2 density under reservoir conditions.',
                effect: 'Relevant for gravity-driven CO2 plume behavior in the reservoir.',
              },
              {
                name: 'Brine Viscosity (cP)',
                description: 'Formation brine viscosity.',
                effect: 'Higher brine viscosity relative to CO2 → more favorable displacement.',
              },
              {
                name: 'Brine Density (kg/m³)',
                description: 'Formation brine density.',
                effect: 'Together with CO2 density, determines buoyancy forces in the CO2 plume.',
              },
              {
                name: 'Viscosity Ratio (μ_brine / μ_CO₂)',
                description: 'Ratio of brine viscosity to CO2 viscosity.',
                effect: 'Directly drives the fractional flow curve on Page 2 and Page 4.',
              },
              {
                name: 'IFT (mN/m)',
                description: 'Interfacial tension between CO2 and brine under reservoir P-T-salinity conditions.',
                effect: 'Controls pe_kpa. Higher IFT → tighter entry pressure → CO2 harder to inject into small pore throats.',
              },
            ],
          },
        ],
      },
      {
        id: 'tables-dimensionless',
        title: 'Dimensionless numbers',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Capillary Number (Nc)',
                description: 'Ratio of viscous forces to capillary forces.',
                effect: 'Higher Nc → viscous forces dominate → capillary trapping is less significant. Lower Nc → capillary forces dominate → stronger CO2 trapping in pores.',
              },
              {
                name: 'Rapoport-Leas Number (RL)',
                description: 'Dimensionless group characterizing the balance of capillary, gravity, and viscous forces in CO2 displacement.',
                effect: 'Higher RL → stronger capillary trapping of CO2 after imbibition. Used to rank zones by trapping efficiency for CO2 storage.',
              },
            ],
          },
        ],
      },
      {
        id: 'tables-endpoints',
        title: 'Process-specific endpoints',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Drainage: swi_drainage (irreducible water saturation), krco2_at_swi_drainage (CO2 endpoint Kr at Swi).',
              'Imbibition: sco2i_imbibition (residual CO2 saturation after imbibition), krw_at_sco2i_imbibition (brine Kr at Sco2i).',
              'Pc: swi_pc (irreducible water saturation used in Pc curve), pe_kpa (capillary entry pressure in kPa), lambda_bc (Brooks-Corey pore size distribution index).',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'ghe-selection',
    label: 'Krel/Pc Selection',
    title: 'Krel and Pc Analogue Selection — Page 4',
    intro:
      'Page 4 shows normalized Kr and Pc curves for each target rock with per-process analogue selection. Use it to focus the curve comparison on relevant analogues and to generate the multi-sheet Excel export.',
    sections: [
      {
        id: 'selection-curves',
        title: 'Normalized curves',
        blocks: [
          {
            type: 'bullets',
            items: [
              'All Kr and Pc curves are normalized to a common scale to enable cross-rock comparison.',
              'ML series is always visible — it cannot be deselected.',
              'Each analogue appears as a separate selectable series.',
              'Removing an analogue from one process (e.g., drainage) does not affect other processes (imbibition or pc) for the same rock.',
              'Selection changes are applied immediately in the current session and are reflected in the Excel export.',
            ],
          },
        ],
      },
      {
        id: 'selection-processes',
        title: 'Processes',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Drainage',
                description: 'CO2 injected into brine-saturated rock. Primary injection direction.',
                effect: 'KrCO2 decreases and Krw increases as Sw increases from Swi toward 1.',
              },
              {
                name: 'Imbibition',
                description: 'Brine re-invades after CO2 injection stops. Post-injection trapping behavior.',
                effect: 'KrCO2 decreases to zero at Sco2i — residual CO2 is trapped. Krw increases to krw_sco2i.',
              },
              {
                name: 'Pc',
                description: 'Capillary pressure curve over the same saturation range as drainage.',
                effect: 'Decreases monotonically from maximum at Swi toward pe_kpa at Sw = 1.',
              },
            ],
          },
        ],
      },
      {
        id: 'selection-fw',
        title: 'Fractional flow charts',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Derived from drainage and imbibition Kr curves using the viscosity ratio from reservoir conditions.',
              'Fw = 0 at Swi (only CO2 flows); Fw = 1 at 1 − Sco2i (only brine flows).',
              'Shows how the fraction of total flow that is brine evolves with water saturation.',
              'A steep Fw curve near Swi indicates CO2 breaks through quickly — fast displacement front.',
            ],
          },
        ],
      },
      {
        id: 'selection-summary',
        title: 'Summary curves',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Average capillary pressure: mean Pc across all selected analogues for each target rock.',
              'Drainage fractional flow summary: Fw computed from average drainage Kr parameters.',
              'Imbibition fractional flow summary: Fw computed from average imbibition Kr parameters.',
            ],
          },
        ],
      },
      {
        id: 'selection-export',
        title: 'Excel export sheets',
        blocks: [
          {
            type: 'paragraph',
            text: 'The Export button generates a multi-sheet Excel workbook reflecting the current analogue selections. Each sheet is described below:',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Input_Petrophysics',
                description: 'GHE1–GHE10 porosity and permeability inputs from Page 1.',
                effect: 'Provides the traceability link between the exported curves and the original rock inputs.',
              },
              {
                name: 'Reservoir_Properties',
                description: 'NaCl ppm, MgCl2 ppm, pressure (MPa), temperature (°C) from Page 1.',
                effect: 'Documents the fluid condition basis for all derived parameters.',
              },
              {
                name: 'ML_Parameters',
                description: 'IFT, viscosity ratio, Swi, Sco2i, co2_exp, water_exp, krco2_swi, krw_sw1, krw_sco2i, pe_kpa, lambda_bc — per rock.',
                effect: 'Full ML base parameter set for each target rock.',
              },
              {
                name: 'Krel_ML',
                description: 'Drainage and imbibition Kr tables (Sw, KrCO2, Krw) for the ML series.',
                effect: 'Ready for direct input to reservoir simulation — ML best estimate.',
              },
              {
                name: 'Pc_ML',
                description: 'Capillary pressure table (Sw, Pc_kPa) for the ML series.',
                effect: 'Ready for direct input to reservoir simulation — ML best estimate.',
              },
              {
                name: 'Krel_Analogue_BestFit',
                description: 'Kr tables for the best-fit analogue (average of the selected analogue set).',
                effect: 'Alternative to ML — uses the analogue ensemble mean.',
              },
              {
                name: 'Pc_Analogue_BestFit',
                description: 'Pc table for the best-fit analogue (average of the selected analogue set).',
                effect: 'Alternative to ML — uses the analogue ensemble mean.',
              },
              {
                name: 'Selected_Analogue_Tables',
                description: 'Full Page 3 analogue property tables for the selected analogues at export time.',
                effect: 'Documents which analogues were used and their petrophysical/fluid properties.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'ghe-ix',
    label: 'IX Tables',
    title: 'IX Tables — Page 5',
    intro:
      'Page 5 generates fluid property tables for reservoir simulator input (IX keyword format). Three table types are available, each generated on demand and downloaded as CSV. Page 5 is independent — it does not require Pages 1–4 to be completed.',
    sections: [
      {
        id: 'ix-enthalpy',
        title: 'Enthalpy Table',
        blocks: [
          {
            type: 'paragraph',
            text: 'Generates component enthalpy values as a function of temperature and pressure, sourced from NIST thermodynamic data.',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Components',
                description: 'List of molecular species (e.g., CO2, H2O, CH4). At least one required.',
                effect: 'Only components with existing NIST datasets are valid. Unrecognized component names will produce an error.',
              },
              {
                name: 'Temperature range (min, max, step) — °C',
                description: 'Defines the temperature grid for the output table.',
                effect: 'All temperature values outside the NIST data coverage will produce no rows. Step must be > 0.',
              },
              {
                name: 'Pressure range (min, max, step) — bar',
                description: 'Defines the pressure grid for the output table.',
                effect: 'Same NIST coverage constraint applies. Step must be > 0.',
              },
            ],
          },
          {
            type: 'bullets',
            items: [
              'Output columns: Pressure (bar), Temperature (°C), [Component] Enthalpy (kJ/mol) per selected component.',
              'Use case: input to thermal EOR or CCS simulation phase behavior models.',
            ],
          },
        ],
      },
      {
        id: 'ix-kvalues',
        title: 'K-Values Table (Crookston Correlation)',
        blocks: [
          {
            type: 'paragraph',
            text: 'Generates vapor-liquid equilibrium K-value coefficients (A–E) per component, based on the Crookston correlation.',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Components',
                description: 'List of molecular species. Must match component names in the Crookston coefficient database.',
                effect: 'Unrecognized names return no rows. At least one component required.',
              },
              {
                name: 'Unit system',
                description: 'Metric (SI units) or Field (oilfield units).',
                effect: 'Selects which coefficient set (A_metric … E_metric or A_oilfield … E_oilfield) is returned. Metric and Field coefficients have different numerical scales.',
              },
            ],
          },
          {
            type: 'bullets',
            items: [
              'Output columns: Component, A, B, C, D, E (coefficient labels follow the selected unit system).',
              'Use case: K-value equations for flash calculations in ECLIPSE-type reservoir simulators.',
            ],
          },
        ],
      },
      {
        id: 'ix-solubility',
        title: 'CO2 Solubility Table',
        blocks: [
          {
            type: 'paragraph',
            text: 'Generates CO2 solubility in brine as gas/water ratio (GWR, sm³/sm³) as a function of temperature and pressure at a fixed salinity.',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Salinity (wt. fraction)',
                description: 'Formation brine salinity as a weight fraction.',
                effect: 'Must match an available salinity value in the solubility database. Non-matching values return no rows.',
              },
              {
                name: 'Temperature range (min, max, step) — °C',
                description: 'Defines the temperature grid for the output table.',
                effect: 'Step must be > 0. Values outside database coverage produce no rows.',
              },
              {
                name: 'Pressure cap (bar)',
                description: 'Maximum pressure for output rows.',
                effect: 'All rows with pressure above the cap are excluded from the output. Must be > 0.',
              },
            ],
          },
          {
            type: 'bullets',
            items: [
              'Output columns: Salinity (wt.fr), Temperature (°C), Pressure (bar), Solution GWR (sm³/sm³).',
              'Use case: CO2 dissolution modeling for aquifer storage capacity and monitoring.',
            ],
          },
        ],
      },
      {
        id: 'ix-workflow',
        title: 'How to generate a table',
        blocks: [
          {
            type: 'bullets',
            items: [
              '1. Select table type: Enthalpy, K-values, or CO2 Solubility.',
              '2. Enter the required parameters for the selected type.',
              '3. Click Generate — a preview of the table appears on screen.',
              '4. Review the preview to confirm the expected rows and columns are present.',
              '5. Click Download CSV to save the file.',
              'Note: generating a new table or switching table types clears the current preview.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'ghe-physics',
    label: 'Fluid Physics',
    title: 'Fluid Physics Reference',
    intro:
      'This section explains the physical principles behind the CO2/brine displacement system modeled in the GCSREToolkit. Understanding these principles helps interpret Kr and Pc curves and calibrate inputs against geological expectations.',
    sections: [
      {
        id: 'physics-system',
        title: 'CO2/brine displacement system',
        blocks: [
          {
            type: 'bullets',
            items: [
              'CO2 is less dense and less viscous than brine under most reservoir conditions — it is the non-wetting phase.',
              'As the non-wetting phase, CO2 occupies the larger pore throats first during drainage. Entry into small throats requires overcoming capillary resistance (pe_kpa).',
              'The viscosity ratio (μ_brine / μ_CO₂) is typically > 1 under reservoir conditions, giving CO2 a mobility advantage over brine.',
              'Residual CO2 trapping (Sco2i) occurs after imbibition — CO2 is permanently trapped in pore bodies, a key CO2 storage mechanism.',
            ],
          },
        ],
      },
      {
        id: 'physics-ift',
        title: 'Interfacial Tension (IFT)',
        blocks: [
          {
            type: 'paragraph',
            text: 'IFT is the surface energy at the CO2–brine interface. It controls how strongly capillary forces resist CO2 entry into pore throats.',
          },
          {
            type: 'bullets',
            items: [
              'Higher IFT → higher pe_kpa → CO2 requires more pressure to invade small pore throats → tighter Pc curve.',
              'Salinity increases IFT: each 100,000 ppm of NaCl adds ~12 mN/m; MgCl2 has double the effect per ppm.',
              'Temperature decreases IFT: rising temperature reduces surface tension.',
              'Pressure slightly increases IFT at typical CCS reservoir conditions.',
              'IFT is clipped to the physically valid range [5, 80] mN/m in all calculations.',
            ],
          },
        ],
      },
      {
        id: 'physics-rqi-fzi',
        title: 'RQI and FZI — Rock Quality Indices',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'RQI = 0.0314 × √(k/φ)  [μm]',
                description: 'Reservoir Quality Index. Combines permeability and porosity into a single flow quality number.',
                effect: 'Higher RQI → better pore-scale flow capacity → lower entry pressure → faster CO2 breakthrough. Used to rank analogues.',
              },
              {
                name: 'PHIZ = φ / (1 − φ)',
                description: 'Normalized Porosity Index. Rescales porosity to remove the 0–1 constraint.',
                effect: 'Intermediate variable used to compute FZI.',
              },
              {
                name: 'FZI = RQI / PHIZ',
                description: 'Flow Zone Indicator. Decouples pore geometry from lithology.',
                effect: 'Primary analogue similarity axis. Analogues with similar FZI to the target rock have similar pore throat geometry and flow behavior.',
              },
            ],
          },
        ],
      },
      {
        id: 'physics-bc',
        title: 'Brooks-Corey Pc model in CO2 context',
        blocks: [
          {
            type: 'paragraph',
            text: 'The toolkit uses the Brooks-Corey capillary pressure model with CO2/brine fluid properties. The form is identical to the water-oil version used in standard SCAL workflows.',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Pc(Sw) = pe_kpa × Swd^(−1/lambda_bc)',
                description: 'Brooks-Corey Pc equation for CO2/brine system.',
                effect: 'pe_kpa sets the lower bound (entry pressure at Sw = 1). lambda_bc controls steepness — higher lambda_bc = steeper = well-sorted pore throats. Lower lambda_bc = flatter = heterogeneous pore sizes.',
              },
              {
                name: 'pe_kpa — Entry Pressure',
                description: 'The threshold CO2 pressure required to begin displacing brine from the largest pore throats. pe_kpa = clip(IFT × 6.0 / φ, 10, 3000).',
                effect: 'Directly proportional to IFT and inversely proportional to porosity. Tight rocks (low φ) or high-salinity brines (high IFT) produce high pe_kpa.',
              },
              {
                name: 'lambda_bc — Pore Size Distribution Index',
                description: 'Brooks-Corey lambda. lambda_bc = clip(1.7 + 0.6 × flow_factor + 0.15 × visco_factor, 0.8, 4.0).',
                effect: 'Well-sorted sandstones: high lambda_bc (narrow pore size distribution). Heterogeneous carbonates or shaly sands: low lambda_bc (broad distribution).',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'ghe-workflow',
    label: 'Workflow',
    title: 'End-to-End Workflow',
    intro:
      'Follow these steps to go from raw target rock data to exported Kr/Pc tables and IX simulator inputs.',
    sections: [
      {
        id: 'workflow-steps',
        title: 'Step-by-step guide',
        blocks: [
          {
            type: 'bullets',
            items: [
              '1. Open Page 1 (GHE Inputs) — enter porosity, permeability, and analogues count for at least one target rock (GHE1–GHE10).',
              '2. Enter reservoir conditions — NaCl ppm, MgCl2 ppm, pressure (MPa), temperature (°C) — then save.',
              '3. Open Page 2 (ML vs Analogues) — curves generate automatically. Review drainage, imbibition, and Pc charts per rock. Verify: KrCO2 at Swi should be < 1; Pc should decrease monotonically from left to right.',
              '4. Open Page 3 (Analogue Tables) — inspect per-rock analogue tables. Verify: similarity scores > 0.5 for useful analogues; RQI/FZI values consistent with expected rock quality.',
              '5. Open Page 4 (Krel/Pc Selection) — use per-process dropdowns to include or exclude specific analogues. Compare the analogue envelope against the ML curve. Use fractional flow charts to assess CO2/brine mobility.',
              '6. Export from Page 4 — click Export to download the multi-sheet Excel workbook: inputs, ML parameters, best-fit Kr/Pc tables, and selected analogue tables.',
              '7. Open Page 5 (IX Tables) independently — select Enthalpy, K-values, or CO2 Solubility; enter parameters; click Generate; preview the table; click Download CSV.',
              '8. Use exported files in simulator — Kr/Pc tables from the Excel export and IX table CSVs are ready for direct input to ECLIPSE-compatible reservoir simulation workflows.',
            ],
          },
        ],
      },
      {
        id: 'workflow-troubleshooting',
        title: 'Troubleshooting common issues',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Page 2 shows no curves',
                description: 'Page 1 inputs were not saved, or all entered rocks have invalid porosity/permeability values.',
                effect: 'Return to Page 1, verify that at least one rock has valid porosity (> 0) and permeability (≥ 0), and save.',
              },
              {
                name: 'KrCO2 or Krw curves are flat or near-zero',
                description: 'Porosity is very high (> 0.4) or permeability is near zero.',
                effect: 'Very high porosity drives krco2_swi close to its lower clip bound (0.15). Very low permeability produces near-zero flow_factor. Verify inputs are physically realistic for the reservoir.',
              },
              {
                name: 'All analogue similarity scores are low (< 0.3)',
                description: 'Target rock petrophysics are outside the range of the analogue database.',
                effect: 'This is expected for extreme porosity or permeability values. Review the analogue tables for the closest matches and note the uncertainty in the analogues used.',
              },
              {
                name: 'IX Table generates no rows',
                description: 'For Enthalpy: component not found in NIST database, or temperature/pressure range outside data coverage. For CO2 Solubility: salinity value does not match a database entry.',
                effect: 'Check component names match exactly. For CO2 Solubility, use a salinity value from the available database options shown in the form.',
              },
            ],
          },
          {
            type: 'note',
            title: 'Page 5 is always available',
            text: 'IX Tables (Page 5) does not require Pages 1–4 to be complete. It can be used as a standalone table generator at any time during the project.',
          },
        ],
      },
    ],
  },
];
